import { generateLookupKey } from 'common/visualizations/helpers/TableDataExportTotalHelpers';
import { type SubtotalsData } from 'common/visualizations/views/agGridReact/types';
import { type Row } from '../type';

export type PivotTargetRow = Record<string, unknown>;
/**
 * Discovers pivot fields from data rows by looking for fields containing '@@'
 */
export const discoverPivotFieldsFromRows = (rows: Row[]): string[] => {
  const pivotFields = new Set<string>();

  for (const row of rows) {
    if (row._rowType === undefined) {
      // Only scan data rows
      for (const key of Object.keys(row)) {
        if (key.includes('@@')) {
          pivotFields.add(key);
        }
      }
    }
  }

  return Array.from(pivotFields);
};

/**
 * Discovers pivot fields from subtotal lookup keys for specific group level and keys
 */
export const discoverPivotFieldsFromSubtotals = (
  subtotalLookup: SubtotalsData | undefined,
  groupKeys: string[],
  level: number,
): string[] => {
  if (!subtotalLookup) {
    return [];
  }

  const pivotFields = new Set<string>();
  const targetKeyPrefix = `${level}:${groupKeys.join('|')}:`;

  for (const [lookupKey] of subtotalLookup) {
    if (lookupKey.startsWith(targetKeyPrefix)) {
      const field = lookupKey.slice(targetKeyPrefix.length);
      if (field.includes('@@')) {
        pivotFields.add(field);
      }
    }
  }

  return Array.from(pivotFields);
};

/**
 * Populates subtotal values from lookup into a row object for given pivot fields
 */
export const populateSubtotalValues = (
  targetRow: PivotTargetRow,
  pivotFields: string[],
  groupKeys: string[],
  level: number,
  subtotalLookup: SubtotalsData | undefined,
): void => {
  if (!subtotalLookup) {
    return;
  }

  for (const field of pivotFields) {
    const lookupKey = generateLookupKey(level, groupKeys, field);
    const value = subtotalLookup.get(lookupKey);

    if (value !== undefined) {
      targetRow[field] = value;
    }
  }
};

/**
 * Creates a group key string for efficient row matching
 */
export const createGroupKeyString = (groupKeys: string[], level: number): string => {
  return `${groupKeys.join('|')}@level${level}`;
};

/**
 * Populates leaf data row with pivoted field values from subtotal lookup
 * Uses leaf level data directly for pivot values
 */
export const populateLeafPivotValues = (
  leafRow: Record<string, unknown>,
  groupKeys: string[],
  leafLevel: number,
  subtotalLookup: SubtotalsData,
): void => {
  // Get pivot field names from leaf level data
  const leafPivotFields = discoverPivotFieldsFromSubtotals(subtotalLookup, groupKeys, leafLevel);
  // Use the leaf level data for pivot values
  populateSubtotalValues(leafRow, leafPivotFields, groupKeys, leafLevel, subtotalLookup);
};
